import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Car, Search, ChevronRight, ChevronLeft, Shield, Clock, Phone, Mail, MessageCircle, CheckCircle, Star, BookOpen } from "lucide-react";
import { Footer } from "../components/Footer";
import { toast } from "sonner";

const envBackend = process.env.REACT_APP_BACKEND_URL;
const API = envBackend ? `${envBackend}/api` : (window.APP_CONFIG?.API_URL || '/api');

const POPULAR_MAKES = [
  "Cualquiera", "Audi", "BMW", "Citroën", "Cupra", "Fiat", "Ford", "Honda", "Hyundai",
  "Kia", "Mazda", "Mercedes-Benz", "Nissan", "Opel", "Peugeot", "Renault", "SEAT",
  "Skoda", "Tesla", "Toyota", "Volkswagen", "Volvo", "Otro"
];

const STEPS = [
  { id: "vehicle", title: "Vehículo", desc: "¿Qué buscas?" },
  { id: "specs", title: "Detalles", desc: "Preferencias" },
  { id: "contact", title: "Contacto", desc: "Tus datos" },
];

export default function OcasionPage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [form, setForm] = useState({
    make: "", model: "", budget: "", min_year: "", max_km: "",
    fuel_type: "", transmission: "", comments: "",
    name: "", phone: "", email: ""
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return true; // Vehicle prefs are optional
    if (step === 1) return true; // Specs are optional
    if (step === 2) return form.name && form.phone;
    return true;
  };

  const handleSubmit = async () => {
    if (!form.name || !form.phone) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/nacional`, {
        ...form,
        budget: parseInt(form.budget) || 0,
        min_year: parseInt(form.min_year) || 0,
        max_km: parseInt(form.max_km) || 0,
      });
      setShowSuccess(true);
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-carbon-pattern">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[rgba(138,138,141,0.1)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/logo-telobajo-new.png" alt="Te Lo Bajo" className="h-14 sm:h-16 w-auto" />
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/importacion" className="flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#E0352B] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-importacion">
              <span className="hidden sm:inline">Importación</span><span className="sm:hidden">Import</span>
            </Link>
            <Link to="/ocasion" className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#006AE0] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-nacionales">
              <span className="hidden sm:inline">Nacionales</span><span className="sm:hidden">Nacional</span>
            </Link>
            <Link to="/coches-disponibles" className="flex items-center gap-1.5 bg-[#34C759] hover:bg-[#2DB84E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-stock">
              <Car className="w-4 h-4" /><span className="hidden sm:inline">Stock</span><span className="sm:hidden">Stock</span>
            </Link>
            <Link to="/blog" className="flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#D4901E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-blog">
              <BookOpen className="w-4 h-4" /><span className="hidden sm:inline">Blog</span><span className="sm:hidden">Blog</span>
            </Link>
          </div>
        </div>
      </nav>
      <section className="relative pt-24 pb-10 px-4 sm:px-6">
        <div className="absolute inset-0 bg-cover bg-bottom bg-no-repeat" style={{ backgroundImage: "url('/hero-bg.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-full px-4 py-1.5 mb-6">
            <Search className="w-4 h-4 text-[#007AFF]" />
            <span className="text-[#007AFF] text-sm font-medium">Mercado nacional</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4" data-testid="ocasion-title">
            Encontramos tu coche <span className="text-[#007AFF]">ideal</span>
          </h1>
          <p className="text-[#8A8A8D] text-sm sm:text-base max-w-xl mx-auto">
            Dinos qué buscas y nosotros nos encargamos. Vehículos de ocasión revisados y garantizados del mercado español.
          </p>
        </div>
      </section>

      {/* Questionnaire */}
      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-xl mx-auto">
          {/* Progress */}
          <div className="flex items-center justify-between mb-8" data-testid="questionnaire-progress">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                    i < step ? 'bg-[#007AFF] border-[#007AFF] text-white' :
                    i === step ? 'border-[#007AFF] text-[#007AFF]' :
                    'border-[#636366] text-[#636366]'
                  }`}>{i < step ? <CheckCircle className="w-5 h-5" /> : i + 1}</div>
                  <span className={`text-xs mt-1.5 ${i <= step ? 'text-white' : 'text-[#636366]'}`}>{s.title}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 mb-5 ${i < step ? 'bg-[#007AFF]' : 'bg-[#636366]/30'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Form card */}
          <div className="glass-card p-5 sm:p-8" data-testid="questionnaire-form">
            {/* Step 1: Vehicle */}
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="text-white font-bold text-lg mb-1">¿Qué coche buscas?</h2>
                <p className="text-[#8A8A8D] text-sm mb-4">Si no tienes claro, déjalo en "Cualquiera" y te asesoramos.</p>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Marca</Label>
                  <Select value={form.make} onValueChange={v => set("make", v)}>
                    <SelectTrigger className="glass-input h-11 mt-1" data-testid="nac-make"><SelectValue placeholder="Selecciona marca" /></SelectTrigger>
                    <SelectContent>{POPULAR_MAKES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Modelo</Label>
                  <Input value={form.model} onChange={e => set("model", e.target.value)} className="glass-input h-11 mt-1" placeholder="ej: Serie 3, Golf, León..." data-testid="nac-model" />
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Presupuesto máximo (€)</Label>
                  <Input type="number" value={form.budget} onChange={e => set("budget", e.target.value)} className="glass-input h-11 mt-1" placeholder="ej: 20000" data-testid="nac-budget" />
                </div>
              </div>
            )}

            {/* Step 2: Specs */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-white font-bold text-lg mb-1">Preferencias</h2>
                <p className="text-[#8A8A8D] text-sm mb-4">Cuéntanos más para afinar la búsqueda.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#C4C4C6] text-sm">Año mínimo</Label>
                    <Input type="number" value={form.min_year} onChange={e => set("min_year", e.target.value)} className="glass-input h-11 mt-1" placeholder="ej: 2018" data-testid="nac-year" />
                  </div>
                  <div>
                    <Label className="text-[#C4C4C6] text-sm">Km máximos</Label>
                    <Input type="number" value={form.max_km} onChange={e => set("max_km", e.target.value)} className="glass-input h-11 mt-1" placeholder="ej: 100000" data-testid="nac-km" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Combustible</Label>
                  <Select value={form.fuel_type} onValueChange={v => set("fuel_type", v)}>
                    <SelectTrigger className="glass-input h-11 mt-1" data-testid="nac-fuel"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                    <SelectContent>
                      {["Cualquiera", "Gasolina", "Diésel", "Híbrido", "Eléctrico", "GLP"].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Cambio</Label>
                  <Select value={form.transmission} onValueChange={v => set("transmission", v)}>
                    <SelectTrigger className="glass-input h-11 mt-1" data-testid="nac-transmission"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                    <SelectContent>
                      {["Cualquiera", "Manual", "Automático"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Extras / Comentarios</Label>
                  <Textarea value={form.comments} onChange={e => set("comments", e.target.value)} className="glass-input mt-1 min-h-[80px]" placeholder="Piel, techo solar, navegación, color preferido..." data-testid="nac-comments" />
                </div>
              </div>
            )}

            {/* Step 3: Contact */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-white font-bold text-lg mb-1">Tus datos de contacto</h2>
                <p className="text-[#8A8A8D] text-sm mb-4">Te contactaremos con las mejores opciones para ti.</p>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Nombre *</Label>
                  <Input value={form.name} onChange={e => set("name", e.target.value)} className="glass-input h-11 mt-1" placeholder="Tu nombre" data-testid="nac-name" />
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Teléfono *</Label>
                  <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="glass-input h-11 mt-1" placeholder="+34 600 000 000" data-testid="nac-phone" />
                </div>
                <div>
                  <Label className="text-[#C4C4C6] text-sm">Email</Label>
                  <Input value={form.email} onChange={e => set("email", e.target.value)} className="glass-input h-11 mt-1" placeholder="tu@email.com" data-testid="nac-email" />
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={() => setStep(s => s - 1)}
                className={`btn-secondary h-11 px-5 ${step === 0 ? 'invisible' : ''}`}
                data-testid="nac-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />Atrás
              </Button>
              {step < 2 ? (
                <Button onClick={() => setStep(s => s + 1)} className="bg-[#007AFF] hover:bg-[#006AE0] text-white h-11 px-6" data-testid="nac-next">
                  Siguiente<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={!canNext() || submitting} className="bg-[#007AFF] hover:bg-[#006AE0] text-white h-11 px-6" data-testid="nac-submit">
                  {submitting ? "Enviando..." : "Enviar solicitud"}
                </Button>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-3 mt-8">
            <div className="glass-card p-4 text-center">
              <Shield className="w-6 h-6 text-[#007AFF] mx-auto mb-2" />
              <p className="text-white text-xs font-bold">Garantía</p>
              <p className="text-[#8A8A8D] text-[10px] mt-1">Revisión completa</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Star className="w-6 h-6 text-[#007AFF] mx-auto mb-2" />
              <p className="text-white text-xs font-bold">Seleccionados</p>
              <p className="text-[#8A8A8D] text-[10px] mt-1">Solo los mejores</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Clock className="w-6 h-6 text-[#007AFF] mx-auto mb-2" />
              <p className="text-white text-xs font-bold">Rápido</p>
              <p className="text-[#8A8A8D] text-[10px] mt-1">Respuesta en 24h</p>
            </div>
          </div>
        </div>
      </section>

      {/* Success dialog */}
      <Dialog open={showSuccess} onOpenChange={() => { setShowSuccess(false); setStep(0); setForm({ make: "", model: "", budget: "", min_year: "", max_km: "", fuel_type: "", transmission: "", comments: "", name: "", phone: "", email: "" }); }}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)] sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Solicitud recibida</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <CheckCircle className="w-16 h-16 text-[#007AFF] mx-auto mb-4" />
            <p className="text-white font-bold text-lg mb-2">Te contactaremos pronto</p>
            <p className="text-[#8A8A8D] text-sm">Hemos recibido tu solicitud. Nuestro equipo buscará las mejores opciones y te contactará en menos de 24 horas.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/importacion" className="flex-1"><Button variant="outline" className="btn-secondary w-full h-11">Importar desde Alemania</Button></Link>
            <Link to="/coches-disponibles" className="flex-1"><Button className="bg-[#007AFF] hover:bg-[#006AE0] text-white w-full h-11">Ver stock disponible</Button></Link>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
